import { toast as sonnerToast, type ExternalToast } from 'sonner';
import type { VariantProps } from 'tailwind-variants';
import {
  CloseIcon,
  InfoIcon,
  CheckCircleOutlineIcon,
  AlertCircleIcon,
  AlertTriangleIcon
} from '@/icons';
import { toastVariants } from './styles';

type ToastBodyProps = React.PropsWithChildren<{
  toastId?: string | number;
}> &
  VariantProps<typeof toastVariants>;

type ExternalToastProps = Pick<
  ExternalToast,
  'duration' | 'position' | 'onAutoClose' | 'onDismiss'
>;

const ToastIcon = {
  error: () => <AlertTriangleIcon width={16} height={16} />,
  info: () => <InfoIcon width={16} height={16} />,
  success: () => <CheckCircleOutlineIcon width={16} height={16} />,
  warning: () => <AlertCircleIcon width={16} height={16} />
};

const ToastBody = ({ toastId, children, type = 'info' }: ToastBodyProps) => {
  const { base, closeButton, description } = toastVariants({ type });
  const Icon = ToastIcon[type];

  return (
    <div className={base({ type })}>
      <Icon />
      <div className={description()}>{children}</div>
      <button className={closeButton()} onClick={() => sonnerToast.dismiss(toastId)}>
        <CloseIcon width={16} height={16} />
        <span className="sr-only">Fechar</span>
      </button>
    </div>
  );
};

export const toast = {
  error: (message: React.ReactNode | string, externalProps?: ExternalToastProps) =>
    sonnerToast.custom(
      (toastId) => (
        <ToastBody toastId={toastId} type="error">
          {message}
        </ToastBody>
      ),
      externalProps
    ),
  info: (message: React.ReactNode | string, externalProps?: ExternalToastProps) =>
    sonnerToast.custom(
      (toastId) => (
        <ToastBody toastId={toastId} type="info">
          {message}
        </ToastBody>
      ),
      externalProps
    ),
  success: (message: React.ReactNode | string, externalProps?: ExternalToastProps) =>
    sonnerToast.custom(
      (toastId) => (
        <ToastBody toastId={toastId} type="success">
          {message}
        </ToastBody>
      ),
      externalProps
    ),
  warning: (message: React.ReactNode | string, externalProps?: ExternalToastProps) =>
    sonnerToast.custom(
      (toastId) => (
        <ToastBody toastId={toastId} type="warning">
          {message}
        </ToastBody>
      ),
      externalProps
    )
};
